import type { DiscussionCardMediaItem } from "@/main/react/card/DiscussionCardMedia";

/**
 * The feed card's media-box aspect ratio: the CLIENT half of a rule implemented twice, here for the
 * React card and in `MainService/.../util/FeedCardMediaBox.java` for `ssr_home_first_card.jte`.
 *
 * **Why twice.** The SSR first card and the React card must reserve the identical box or the most
 * visible card in the product shifts the moment React mounts over it. The alternatives were both
 * worse: letting CSS clamp (`aspect-ratio: clamp(min, var(--r), max)`) is one implementation but
 * fails to a COLLAPSED box if any client drops the declaration, and persisting a pre-clamped ratio
 * bakes a presentation constant into storage. The two copies are pinned against each other by
 * `feedCardBoxParity.spec.ts`, which reads the bounds out of both files and compares them.
 *
 * **Why clamp at all.** Unclamped, a 1:20 panorama eats a viewport and a 20:1 is a sliver. 4:5
 * (portrait) to 16:9 (landscape) is where Instagram and Reddit both settled. Outside the clamp the
 * image letterboxes inside the box exactly as it does today, so a clamped card is never worse than
 * the fixed box it replaces.
 *
 * **Null is a first-class answer, permanently.** Failed resizes, dropped completion messages,
 * external hot-linked images and every provider embed will never carry dimensions. `null` means "use
 * the fixed `--feed-card-media-reserved-height` box" — the normal path, not a degraded one.
 *
 * Design: `designs/01-now/2026-08-01-media-dimension-backfill-and-aspect-honest-feed-card-design.md`.
 */

/** Portrait clamp — 4:5. Mirrored by `FeedCardMediaBox.ASPECT_MIN` and `--feed-card-media-aspect-min`. */
export const FEED_CARD_MEDIA_ASPECT_MIN = 0.8;

/** Landscape clamp — 16:9. Mirrored by `FeedCardMediaBox.ASPECT_MAX` and `--feed-card-media-aspect-max`. */
export const FEED_CARD_MEDIA_ASPECT_MAX = 1.7778;

/** The class the box always carries. */
export const FEED_CARD_MEDIA_BOX_CLASS = "feed-card-media-box";

/** The additive modifier that swaps the fixed height for the inherited aspect ratio. */
export const FEED_CARD_MEDIA_BOX_ASPECT_CLASS = "feed-card-media-box--aspect";

/**
 * Each carousel slide. It needs its OWN height rather than `h-full`: the shadcn carousel puts two
 * un-styleable divs between the box and the slide (`CarouselContent`'s ref wrapper is a hardcoded
 * `overflow-hidden`), so a percentage height would resolve against an auto-height ancestor and
 * collapse. The rule pair in `01-base.css` gives it the same fixed-or-aspect treatment as the box.
 */
export const FEED_CARD_MEDIA_SLIDE_CLASS = "feed-card-media-slide";

/**
 * The custom property the clamped ratio is written to, inline, on the box. A property rather than
 * `aspect-ratio` directly because it **inherits** to the slide, so the box and its slides cannot
 * disagree without the value being authored twice.
 */
export const FEED_CARD_MEDIA_ASPECT_PROPERTY = "--feed-card-media-aspect-ratio";

function toPositiveInteger(value: unknown): number | null {
    if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
        return null;
    }
    return Math.round(value);
}

/**
 * The clamped width/height ratio as a CSS `aspect-ratio` value, or null when the dimensions are
 * unknown or not a usable measurement. Rendered to 4 decimal places so this and the server emit
 * byte-identical strings.
 */
export function resolveFeedCardMediaAspectRatio(
    width: unknown,
    height: unknown,
): string | null {
    const resolvedWidth = toPositiveInteger(width);
    const resolvedHeight = toPositiveInteger(height);
    if (resolvedWidth === null || resolvedHeight === null) {
        return null;
    }

    const ratio = resolvedWidth / resolvedHeight;
    if (!Number.isFinite(ratio) || ratio <= 0) {
        return null;
    }

    const clamped = Math.min(FEED_CARD_MEDIA_ASPECT_MAX, Math.max(FEED_CARD_MEDIA_ASPECT_MIN, ratio));
    // Round-half-up to 4 dp and let the default number formatting drop trailing zeros. This is
    // deliberately written to match BigDecimal.setScale(4, HALF_UP).stripTrailingZeros()
    // .toPlainString() on the Java half exactly, rather than toFixed(4) — the two renderers emit this
    // string into the same attribute on the same element, so they must agree character for character.
    return String(Math.round(clamped * 10000) / 10000);
}

/**
 * The clamped aspect ratio of the FIRST card media item, or null.
 *
 * Item `[0]` always, never a per-slide ratio: it is the slide the SSR card reserves for and the LCP
 * candidate, and resizing the box as the carousel advances would be a layout shift — the exact thing
 * this box exists to prevent.
 */
export function resolveFeedCardMediaBoxAspectRatio(
    items: readonly DiscussionCardMediaItem[],
): string | null {
    const first = items.length > 0 ? items[0] : null;
    if (!first) {
        return null;
    }
    return resolveFeedCardMediaAspectRatio(first.width, first.height);
}
